import { create } from "zustand";
import * as Sentry from "@sentry/nextjs";
import api from "@/lib/api";
import type { AuthResponse, AuthUser, CurrentUserResponse } from "@/types";

const AUTH_BROADCAST_KEY = "hba-auth-sync";

function mapCurrentUser(data: CurrentUserResponse): AuthUser {
  return {
    id: data.id,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    fullName: data.fullName,
    roles: data.roles,
    avatarUrl: data.avatarUrl,
  };
}

function setSentryUser(data: Pick<AuthUser, "id" | "email" | "fullName" | "roles">) {
  Sentry.setUser({
    id: data.id ?? data.email,
    email: data.email,
    username: data.fullName,
    role: data.roles?.[0],
  });
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isReady: boolean;
  initialize: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isReady: false,

  initialize: async () => {
    try {
      const { data } = await api.get<CurrentUserResponse>("/api/auth/me");
      setSentryUser(mapCurrentUser(data));
      set({
        user: mapCurrentUser(data),
        isAuthenticated: true,
        isReady: true,
      });
    } catch {
      set((state) => state.isAuthenticated
        ? { ...state, isReady: true }
        : { user: null, isAuthenticated: false, isReady: true });
    }
  },

  refreshUser: async () => {
    try {
      const { data } = await api.get<CurrentUserResponse>("/api/auth/me");
      setSentryUser(mapCurrentUser(data));
      set({
        user: mapCurrentUser(data),
        isAuthenticated: true,
        isReady: true,
      });
    } catch {
      set((state) => state.isAuthenticated
        ? { ...state, isReady: true }
        : { user: null, isAuthenticated: false, isReady: true });
    }
  },

  setUser: (user) => set({
    user,
    isAuthenticated: !!user,
    isReady: true,
  }),

  login: async (email, password) => {
    const { data } = await api.post<AuthResponse>("/api/auth/login", {
      email,
      password,
    });

    setSentryUser({
      email: data.email,
      fullName: data.fullName,
      roles: data.roles,
    });

    set({
      isAuthenticated: true,
      isReady: true,
      user: {
        email: data.email,
        fullName: data.fullName,
        roles: data.roles,
      },
    });

    if (typeof window !== "undefined") {
      localStorage.setItem(AUTH_BROADCAST_KEY, JSON.stringify({ action: "login", timestamp: Date.now() }));
    }
  },

  logout: async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // Oturum zaten düşmüş olabilir
    }

    Sentry.setUser(null);
    set({ user: null, isAuthenticated: false, isReady: true });

    if (typeof window !== "undefined") {
      localStorage.setItem(AUTH_BROADCAST_KEY, JSON.stringify({ action: "logout", timestamp: Date.now() }));
    }
  },
}));

// Cross-tab auth sync — logout/login yapıldığında diğer sekmeler bilgilendirilir
export function subscribeAuthBroadcast(callback: (action: "login" | "logout") => void) {
  if (typeof window === "undefined") return () => {};

  const handler = (e: StorageEvent) => {
    if (e.key === AUTH_BROADCAST_KEY && e.newValue) {
      try {
        const payload = JSON.parse(e.newValue);
        if (payload.action === "login" || payload.action === "logout") {
          callback(payload.action);
        }
      } catch {
        // ignore invalid payload
      }
    }
  };

  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
